import { Button, Flex, Label, ModalManagerContext, Text, Image, Grid, TextArea } from '@pega/cosmos-react-core';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { useDispatch, useSelector } from 'react-redux';
import { deleteAccount, getAccountDetails } from './VonageApis';
import { useNavigate, useParams } from 'react-router-dom';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import * as Logos from '../../../constants/images';
import { appState, confirmationAlertProps } from '../../../interfaces/common';
import useLocalTranslation from '../../../hooks/useLocalTranslation';

type AccountDetails = {
  applicationId: string;
  privateKey: string;
  lastUpdate: string;
  name: string;
  phoneNumber: string;
};

const Vonage: React.FC<any> = (props) => {
  const { id: connectionId }: any = useParams();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const initialState: AccountDetails = {
    applicationId: '',
    privateKey: '',
    lastUpdate: '',
    name: '',
    phoneNumber: '',
  };
  const [accountDetails, setaccountDetails] = useState(initialState);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const accountType = connectionId.includes('whatsapp') ? 'whatsapp' : 'sms';
  const endpoint = accountType === 'sms' ? 'vonage-sms' : 'vonage-whatsapp';

  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: accountDetails?.name,
        deleteChannel: deleteChannel,
        ...props,
      },
      { alert: true },
    );
  };

  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setaccountDetails(response?.data);
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId, endpoint);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      const accountbannerInfo = endpoint === 'vonage-sms' ? 'Vonage SMS' : 'Vonage WhatsApp';
      dispatch(updateBannerInfo(`${translate(accountbannerInfo)} ${translate('account_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [sessionId, connectionId, dispatch, navigate, translate, endpoint]);

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  const headerElement = () => {
    return (
      accountDetails?.name && (
        <Text variant="h1" as="h1">
          {accountDetails?.name}
        </Text>
      )
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountDetails?.name && (
        <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('connection')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Text variant="secondary">
                {accountType === 'sms' ? translate('Vonage SMS') : translate('Vonage WhatsApp')}
              </Text>
              <Image
                alt={accountType === 'sms' ? 'SMS' : 'WhatsApp'}
                src={accountType === 'sms' ? Logos.SMSLogo : Logos.WhatsappLogo}
                width="25px"
                height="30px"
              />
            </Flex>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('phone_number')}</Label>
            <Text variant="secondary">{accountDetails?.phoneNumber}</Text>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(3, 33%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('application_id')}</Label>
            <Text variant="secondary">{accountDetails?.applicationId}</Text>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('webhook')}</Label>
            <Text variant="secondary">
              {process.env.REACT_APP_INCOMING_WEBHOOK}/{endpoint}
            </Text>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('private_key')}</Label>
            <Flex container={{ itemGap: 2, alignItems: 'end' }}>
              <TextArea
                name="privateKey"
                type="text"
                label=""
                resizable={true}
                value={atob(accountDetails?.privateKey)}
                disabled={true}
              />
            </Flex>
          </Grid>
          <Flex style={{ paddingTop: '0.5rem' }}>
            <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
          </Flex>
        </Flex>
      )}
    </SectionRenderTemplate>
  );
};

export default Vonage;
